import { 
  getLocationBySlug, 
  isLocationQualified,
  getStateBySlug,
  getCountryBySlug,
  SupportedLocation,
  CountryRecord,
  getJobsLocationUrl as regGetJobsUrl,
  getHiringLocationUrl as regGetHiringUrl,
  getInternshipsLocationUrl as regGetInternshipsUrl
} from "./locationRegistry";

export const RESERVED_PUBLIC_SLUGS = new Set([
  "locations",
  "explore",
  "guides",
  "industries",
  "features",
  "methodology",
  "reports",
  "about",
  "contact",
  "faq",
  "docs",
  "documentation",
  "login",
  "signup",
  "dashboard",
  "app",
  "saved",
  "account",
  "billing",
  "signup-success",
  "login-success",
  "privacy",
  "terms",
  "find-jobs",
  "search",
  "null",
  "undefined",
  "placeholder",
  "slug",
  "city-state-placeholder",
  "hiring-signals-explained",
  "hidden-jobs",
  "verify-email",
  "verify-email-page",
  "compare",
  "states",
  "businesses-hiring-near-me",
  "first-jobs",
  "local-internships",
  "how-it-works",
  "how-to-contact-local-businesses-for-a-job",
  "how-to-prioritize-businesses",
  "how-to-organize-local-job-search",
  "how-to-ask-if-hiring",
  "sitemaps",
  "api"
]);

export const US_STATE_CODES: Record<string, string> = {
  al: "Alabama", ak: "Alaska", az: "Arizona", ar: "Arkansas", ca: "California",
  co: "Colorado", ct: "Connecticut", de: "Delaware", fl: "Florida", ga: "Georgia",
  hi: "Hawaii", id: "Idaho", il: "Illinois", in: "Indiana", ia: "Iowa",
  ks: "Kansas", ky: "Kentucky", la: "Louisiana", me: "Maine", md: "Maryland",
  ma: "Massachusetts", mi: "Michigan", mn: "Minnesota", ms: "Mississippi", mo: "Missouri",
  mt: "Montana", ne: "Nebraska", nv: "Nevada", nh: "New Hampshire", nj: "New Jersey",
  nm: "New Mexico", ny: "New York", nc: "North Carolina", nd: "North Dakota", oh: "Ohio",
  ok: "Oklahoma", or: "Oregon", pa: "Pennsylvania", ri: "Rhode Island", sc: "South Carolina",
  sd: "South Dakota", tn: "Tennessee", tx: "Texas", ut: "Utah", vt: "Vermont",
  va: "Virginia", wa: "Washington", wv: "West Virginia", wi: "Wisconsin", wy: "Wyoming",
  dc: "District of Columbia", pr: "Puerto Rico"
};

export function isReservedPublicSlug(slug: string): boolean {
  if (!slug) return true;
  const clean = slug.toLowerCase().trim().replace(/^\/+|\/+$/g, "");
  return RESERVED_PUBLIC_SLUGS.has(clean);
}

export type LocationResolveType =
  | "reserved"     // e.g. "locations"
  | "supported"    // e.g. "peoria-il" (in registry & qualified)
  | "state"        // e.g. "illinois" or "il"
  | "country"      // e.g. "france", "canada", "united-arab-emirates"
  | "unsupported"  // valid city-state e.g. "springfield-mo", but not in registry
  | "invalid";     // malformed e.g. "undefined", "null", "fake-city-xx"

export interface LocationResolveResult {
  type: LocationResolveType;
  normalizedSlug: string;
  locationRecord: SupportedLocation | null;
  stateRecord: { stateName: string; stateAbbreviation: string; locationCount: number } | null;
  countryRecord?: CountryRecord | null;
  displayName: string;
  cityName?: string;
  stateAbbr?: string;
  stateName?: string;
  countryName?: string;
  countryCode?: string;
}

export function resolveLocationSlug(rawSlug: string): LocationResolveResult {
  if (!rawSlug) {
    return {
      type: "invalid",
      normalizedSlug: "",
      locationRecord: null,
      stateRecord: null,
      displayName: ""
    };
  }

  let slug = rawSlug.trim().toLowerCase();
  slug = slug.replace(/^\/+|\/+$/g, "");
  slug = slug.replace(/^(jobs-in-|local-jobs-|local-internships-|internships-in-|businesses-hiring-in-)/, "");
  slug = slug.replace(/^locations\//, "");
  slug = slug.replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-");

  if (!slug || isReservedPublicSlug(slug)) {
    return {
      type: "reserved",
      normalizedSlug: slug,
      locationRecord: null,
      stateRecord: null,
      displayName: ""
    };
  }

  // Handle typo corrections
  if (slug.includes("chigago")) {
    slug = slug.replace("chigago", "chicago");
  }

  // 1. Check supported location in LOCATION_REGISTRY
  const loc = getLocationBySlug(slug);
  if (loc && isLocationQualified(loc)) {
    return {
      type: "supported",
      normalizedSlug: loc.citySlug,
      locationRecord: loc,
      stateRecord: null,
      displayName: `${loc.cityName}, ${loc.stateAbbreviation}`,
      cityName: loc.cityName,
      stateAbbr: loc.stateAbbreviation,
      stateName: loc.stateName,
      countryName: loc.countryName,
      countryCode: loc.countryCode
    };
  }

  // 2. Check State Hub match (e.g. "illinois", "il", "texas", "tx")
  const stateMatch = getStateBySlug(slug);
  if (stateMatch) {
    return {
      type: "state",
      normalizedSlug: stateMatch.stateName.toLowerCase().replace(/\s+/g, "-"),
      locationRecord: null,
      stateRecord: stateMatch,
      displayName: stateMatch.stateName,
      stateAbbr: stateMatch.stateAbbreviation,
      stateName: stateMatch.stateName,
      countryName: stateMatch.countryName,
      countryCode: stateMatch.countryCode
    };
  }

  // 3. Check Country match (e.g. "france", "canada", "brazil", "united-arab-emirates")
  const countryMatch = getCountryBySlug(slug);
  if (countryMatch) {
    return {
      type: "country",
      normalizedSlug: countryMatch.countrySlug,
      locationRecord: null,
      stateRecord: null,
      countryRecord: countryMatch,
      displayName: countryMatch.countryName,
      countryName: countryMatch.countryName,
      countryCode: countryMatch.countryCode
    };
  }

  // 4. Check valid city-state slug format (e.g. "springfield-mo", "los-angeles-ca", "st-louis-mo")
  const cityStateMatch = slug.match(/^([a-z0-9-]+)-([a-z]{2})$/);
  if (cityStateMatch) {
    const rawCityPart = cityStateMatch[1];
    const stateAbbrPart = cityStateMatch[2];

    if (US_STATE_CODES[stateAbbrPart] && !isReservedPublicSlug(rawCityPart) && rawCityPart !== "undefined" && rawCityPart !== "null" && rawCityPart !== "fake-city") {
      const formattedCity = rawCityPart
        .split("-")
        .map(part => {
          if (part === "st") return "St.";
          if (part === "ft") return "Ft.";
          return part.charAt(0).toUpperCase() + part.slice(1);
        })
        .join(" ");

      const stateName = US_STATE_CODES[stateAbbrPart];
      const stateAbbr = stateAbbrPart.toUpperCase();

      return {
        type: "unsupported",
        normalizedSlug: slug,
        locationRecord: null,
        stateRecord: null,
        displayName: `${formattedCity}, ${stateAbbr}`,
        cityName: formattedCity,
        stateAbbr,
        stateName,
        countryName: "United States",
        countryCode: "US"
      };
    }
  }

  // 5. Invalid / malformed slug
  return {
    type: "invalid",
    normalizedSlug: slug,
    locationRecord: null,
    stateRecord: null,
    displayName: ""
  };
}

export function normalizeCitySlug(rawSlug: string): string {
  const res = resolveLocationSlug(rawSlug);
  return res.normalizedSlug || rawSlug;
}

export function isPublishedLocation(slug: string): boolean {
  const res = resolveLocationSlug(slug);
  return res.type === "supported";
}

export function getLocationDisplayName(slug: string): string {
  const res = resolveLocationSlug(slug);
  return res.displayName || "";
}

export function getCanonicalLocationPath(slug: string, variant: "jobs" | "businesses-hiring" | "internships" = "jobs"): string {
  const res = resolveLocationSlug(slug);
  if (res.type === "supported" || res.type === "state") {
    return `/${variant}/${res.normalizedSlug}`;
  }
  if (res.type === "reserved") {
    return `/${variant}/locations`;
  }
  return `/${variant}/${res.normalizedSlug || "locations"}`;
}

export function getLocationSearchDestination(slug: string): string {
  const res = resolveLocationSlug(slug);
  if (res.normalizedSlug && res.type !== "invalid" && res.type !== "reserved") {
    return `/jobs/${res.normalizedSlug}`;
  }
  return "/jobs/locations";
}

export function shouldIndexLocationPage(slug: string): boolean {
  const res = resolveLocationSlug(slug);
  return res.type === "supported" || res.type === "state";
}

export function getJobsLocationUrl(citySlug: string): string {
  return regGetJobsUrl(citySlug);
}

export function getHiringLocationUrl(citySlug: string): string {
  return regGetHiringUrl(citySlug);
}

export function getInternshipsLocationUrl(citySlug: string): string {
  return regGetInternshipsUrl(citySlug);
}

import { getSeoRedirect } from "./seoRedirects";

export interface LegacyRouteMatch {
  targetPath: string;
  variant: "jobs" | "businesses-hiring" | "internships";
  citySlug: string;
}

export function getStandardizedLocationRedirect(pathname: string): LegacyRouteMatch | null {
  if (!pathname) return null;
  const redirect = getSeoRedirect(pathname);
  if (redirect) {
    let variant: "jobs" | "businesses-hiring" | "internships" = "jobs";
    if (redirect.to.startsWith("/businesses-hiring/")) variant = "businesses-hiring";
    if (redirect.to.startsWith("/internships/")) variant = "internships";

    const parts = redirect.to.split("/");
    const citySlug = parts[parts.length - 1] || "";

    return {
      targetPath: redirect.to,
      variant,
      citySlug
    };
  }

  return null;
}
